// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import {VNode} from 'snabbdom';
import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  Placement as FloatingPlacement,
  shift,
} from '@floating-ui/react';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {portalify} from 'common/util/experimental-portal';
import timing from 'common/stylesheets/mixins/timing.json';
import {panel2react} from 'common/util/react';
import {isTestEnv} from 'common/report/util/env';
import type {IronContextRegistry} from 'common/report/context-registry';
import {LocalizationStore} from 'common/i18n/store';
import {i18next} from 'common/i18n';

import {
  ArrowType,
  DisplaySize,
  DisplayType,
  Placement,
  PositioningStrategy,
  TooltipAttrs,
  TooltipJsxProps,
  TooltipParams,
  TooltipState,
  TooltipTextAlign,
} from './types';
import {template} from './template';
import css from './index.inline.styl';

const VISIBILITY_HIDDEN = `hidden`;
const VISIBILITY_TRANSITIONING = `transitioning`;
const VISIBILITY_VISIBLE = `visible`;

// defaults right and left positioning to floating-ui's top edge alignment
const FLOATING_POSITION_OVERRIDES: {[key in Placement]: FloatingPlacement} = {
  [Placement.Left]: `left-start`,
  [Placement.Right]: `right-start`,
  [Placement.RightCenter]: `right`,
  [Placement.LeftCenter]: `left`,
  [Placement.RightEnd]: `right-end`,
  [Placement.LeftEnd]: `left-end`,
  [Placement.Top]: `top`,
  [Placement.Bottom]: `bottom`,
};

const DEFAULT_OFFSET_SENTINEL = -1;

/**
 * When a delay is provided, after a tooltip is shown with initial in-delay, hovering over neighbor
 * elements with tooltips (while the previous tooltip is still shown with an out-delay or not) will not have a delay effect.
 *
 * This behavior is achieved by keeping a reference to the currently active tooltip so the next tooltip knows it's part of a "chain",
 * and hide the previous tooltip when it has an out-delay
 */
let currentlyActiveTooltip: Tooltip = null;

export class Tooltip extends Component<
  TooltipState,
  TooltipAttrs,
  unknown,
  unknown,
  IronContextRegistry,
  TooltipParams
> {
  private declare _listeners;
  public declare _parentEl;
  private declare _pendingPositionTooltipRaf;
  private declare _pendingPositionTooltipTimeout;
  private declare _pendingHideTooltipTimeout;
  private elmRefs: Record<string, HTMLDivElement> = {};
  private floaterCleanup: () => void = null;

  /** for expensive components/templates that will only render when tooltip is visible */
  public declare lazyContentRenderer?: (i18n: LocalizationStore) => VNode;

  static get attrsSchema(): AttrsSchema<TooltipAttrs> {
    return {
      ...super.attrsSchema,
      'is-tooltip-tour': `boolean`,
      'always-show': `boolean`,
      'force-show': `boolean`,
      'non-blocking': {
        type: `boolean`,
        description: `Whether to show the tooltip without blocking mouse events on the parent element, useful if tooltips are within table rows`,
      },
      'break-word': {
        type: `boolean`,
        description: `Whether to break within words for multiline tooltips (helpful for long URLs)`,
      },
      skidding: {type: `number`, default: 0, description: `Amount of shifting parallel to parent`},
      offset: {type: `number`, default: DEFAULT_OFFSET_SENTINEL},
      'max-width': {type: `string`, default: null},
      placement: {type: `string`, default: Placement.Top, enum: Object.values(Placement)},
      type: {type: `string`, default: DisplayType.Default, enum: Object.values(DisplayType)},
      'arrow-type': {type: `string`, default: ArrowType.Default, enum: Object.values(ArrowType)},
      'delay-in': {
        type: `number`,
        description: `value in ms to delay showing tooltip`,
        default: timing[`$mp-default-tooltip-delay-in`],
      },
      'delay-out': {
        type: `number`,
        description: `value in ms to delay hiding tooltip`,
        default: timing[`$mp-default-tooltip-delay-out`],
      },
      'image-src': {type: `string`, description: `image to show on top of tooltip, used for tutorials.`},
      size: {type: `string`, enum: Object.values(DisplaySize), default: DisplaySize.Fit},
      text: {type: `string`, description: `display formatted text with links inside the tooltip.`},
      'position-strategy': {
        type: `string`,
        enum: Object.values(PositioningStrategy),
        default: PositioningStrategy.Fixed,
        description: `Defines how the tooltip is rendered, fixed to the document level or absolute relative to its offset parent`,
      },
      'allowed-flip-directions': {
        type: `json`,
        default: null,
        description: `Defines preferred flipping directions for whenever the dropdown cannot be fitted in view`,
      },
      'text-align': {
        type: `string`,
        enum: Object.values(TooltipTextAlign),
        default: TooltipTextAlign.Center,
      },
    };
  }

  constructor() {
    super();
    // track added listeners so we can remove them in disconnectedCallback and be GC friendly
    this._listeners = new Map();
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultContexts: {
        i18n: new LocalizationStore(i18next),
      },
      contexts: [`i18n` as const],
      defaultState: {
        visibility: VISIBILITY_HIDDEN,
        isFirstActiveTooltip: true,
      },
      params: {
        text: String,
        placement: String,
      },
      helpers: {
        setElmRef:
          (name: string) =>
          ({elm}) =>
            (this.elmRefs[name] = elm),
        VISIBILITY_HIDDEN,
        VISIBILITY_TRANSITIONING,
        VISIBILITY_VISIBLE,
        stopPropagation: (ev) => {
          ev.stopPropagation();
        },
        getTooltipEl: (): HTMLDivElement | null => this.elmRefs[`main`] || this.el.querySelector(`.mp-tooltip-main`),
        shouldAnimateTooltip: () => !!this.attr(`delay-in`) && this.state.isFirstActiveTooltip,
        getOffset: () => {
          if (this.attr(`offset`) === DEFAULT_OFFSET_SENTINEL) {
            return this.attr(`arrow-type`) === ArrowType.Default ? 8 : 41;
          }
          return this.attr(`offset`);
        },
      },
    };
  }

  updateFloatingPosition = () => {
    const arrowEl = this.el.querySelector<HTMLElement>(`.mp-tooltip-arrow`);

    computePosition(this.parentEl, this, {
      placement: FLOATING_POSITION_OVERRIDES[this.params.placement || this.attr(`placement`)],
      strategy: this.attr(`position-strategy`),
      middleware: [
        // moves the tooltip across the viewport so that it prevents overflowing the screen
        shift({
          padding: 8,
        }),

        // pushes the floating element away from its reference
        offset({
          mainAxis: this.helpers.getOffset(),
          crossAxis: this.attr(`skidding`),
        }),

        // adds an arrow pointer around the floating element
        arrowEl && arrow({padding: 4, element: arrowEl}),

        // prevents the floating element to overflow the screen
        flip({
          rootBoundary: `viewport`,
          fallbackStrategy: `bestFit`,
          padding: {top: 0},
          ...(this.attr(`allowed-flip-directions`)
            ? {
                fallbackPlacements: this.attr(`allowed-flip-directions`)?.map(
                  (direction) => FLOATING_POSITION_OVERRIDES[direction],
                ),
              }
            : {}),
        }),
      ],
    }).then(({x, y, placement: finalPlacement, strategy, middlewareData}) => {
      Object.assign(this.style, {
        left: `${x}px`,
        top: `${y}px`,
        position: strategy,
      });
      if (this.attr(`non-blocking`)) {
        this.style.pointerEvents = `none`;
      }

      this.setAttribute(`data-placement`, finalPlacement);

      if (middlewareData.arrow) {
        const {x: arrowX, y: arrowY} = middlewareData.arrow;
        Object.assign(arrowEl.style, {
          left: typeof arrowX === `number` ? `${arrowX}px` : null,
          top: typeof arrowY === `number` ? `${arrowY}px` : null,
        });
      }
    });
  };

  /**
   * Setups the positioning library for the dropdown, for more info see:
   * https://floating-ui.com/docs/getting-started
   */
  setupFloatingUI() {
    this.floaterCleanup = autoUpdate(this.parentEl, this, this.updateFloatingPosition);

    return this.floaterCleanup;
  }

  show() {
    // Avoid race condition when mouse leaves and enters within the delay window
    if (currentlyActiveTooltip === this) {
      return;
    }

    this.update({
      visibility: VISIBILITY_TRANSITIONING,
    });

    const delay = this.attr(`delay-in`);
    const makeTooltipVisible = () => {
      if (delay && !(this.attr(`always-show`) || this.attr(`force-show`))) {
        this.update({isFirstActiveTooltip: !currentlyActiveTooltip});
        currentlyActiveTooltip?.hideAndCancelPositioning();
        // we're not aliasing 'this', we're storing a module-level reference to the current component
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        currentlyActiveTooltip = this;
      }

      this.setupFloatingUI();
      this.updateFloatingPosition();

      this.update({
        visibility: VISIBILITY_VISIBLE,
      });
      this.dispatchEvent(new CustomEvent(`showed`));
    };
    if (delay && !currentlyActiveTooltip) {
      this._pendingPositionTooltipTimeout = window.setTimeout(() => {
        makeTooltipVisible();
      }, delay);
    } else {
      this._pendingPositionTooltipRaf = window.requestAnimationFrame(() => {
        makeTooltipVisible();
      });
    }
  }

  hide(ev: MouseEvent) {
    // hide behavior should be disabled on tooltip tour mode mode since the tooltip-tour element
    // handles it's own visibility behavior
    if (this.attr(`is-tooltip-tour`) || this.attr(`force-show`)) return;

    if (ev.relatedTarget === this) {
      return;
    }
    this.cancelPendingPositionTooltipIfNeeded();

    const delay = this.attr(`delay-out`);
    const hideTooltip = () => {
      this.update({visibility: VISIBILITY_HIDDEN});
      currentlyActiveTooltip = null;

      /** to prevent floating-ui from modifying styles before the HIDDEN state gets a chance
       * to render, we wait for the next rendering cycle to cleanup floating-ui
       */
      requestAnimationFrame(() => {
        this.cleanupFloatingUI();
      });
    };
    if (delay) {
      this._pendingHideTooltipTimeout = setTimeout(() => hideTooltip(), delay);
    } else {
      hideTooltip();
    }
  }

  /**
   * Used for adding/removing event listeners
   */
  get parentEl() {
    return this._parentEl || this.parentNode;
  }

  set parentEl(parentEl) {
    this._parentEl = parentEl;
    if (this.initialized) {
      this.render();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    // From MDN doc https://developer.mozilla.org/en-US/docs/Web/Web_Components/Using_custom_elements
    // "connectedCallback may be called once your element is no longer connected, use
    // Node.isConnected to make sure."
    // This is happening when dragging query entry rows in the query builder. As a result,
    // parentNode is unexpectedly null, leading to a JS exception.
    //
    // Secondary check for this.parentNode existing because it is intermittently null in iso tests as of panel@5.7.0
    // which fixed setting isConnected before connectedCallback(). TODO(jakub): investigate why this is happening.
    if (!this.isConnected || !this.parentNode) {
      return;
    }

    this.show = this.show.bind(this);
    this.hide = this.hide.bind(this);

    this.render();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (this.initialized) {
      this.render();
    }
  }

  render() {
    this.removeListenersIfNeeded();
    if (this.attr(`always-show`) || this.attr(`force-show`)) {
      this.show();
    } else {
      if (this.state.visibility === VISIBILITY_VISIBLE) {
        this.show();
      }
      this._addEventListener(this.parentEl, `mouseenter`, this.show);
      this._addEventListener(this.parentEl, `mousedown`, this.hide);
      this._addEventListener(this.parentEl, `mouseleave`, this.hide);
    }
  }

  _addEventListener(el, event, listener) {
    el.addEventListener(event, listener);
    if (!this._listeners.has(el)) {
      this._listeners.set(el, {[event]: listener});
    } else {
      this._listeners.get(el)[event] = listener;
    }
  }

  removeListenersIfNeeded() {
    for (const [el, listeners] of this._listeners.entries()) {
      for (const [event, listener] of Object.entries(listeners)) {
        el.removeEventListener(event, listener);
      }
    }
  }

  cancelPendingPositionTooltipIfNeeded() {
    if (this._pendingPositionTooltipRaf) {
      window.cancelAnimationFrame(this._pendingPositionTooltipRaf);
      this._pendingPositionTooltipRaf = null;
    }

    if (this._pendingPositionTooltipTimeout) {
      window.clearTimeout(this._pendingPositionTooltipTimeout);
      this._pendingPositionTooltipTimeout = null;
    }

    if (this._pendingHideTooltipTimeout) {
      window.clearTimeout(this._pendingHideTooltipTimeout);
      this._pendingHideTooltipTimeout = null;
    }
  }

  disconnectedCallback() {
    this.cleanupFloatingUI();
    super.disconnectedCallback();
    this.removeListenersIfNeeded();
    this.cancelPendingPositionTooltipIfNeeded();
    if (currentlyActiveTooltip === this) {
      currentlyActiveTooltip = null;
    }
  }

  cleanupFloatingUI() {
    if (this.floaterCleanup) {
      this.floaterCleanup();
      this.floaterCleanup = null;
      this.removeAttribute(`data-placement`);
      this.removeAttribute(`style`);
    }
  }

  // Called externally by reference to this component
  public hideAndCancelPositioning() {
    this.update({visibility: VISIBILITY_HIDDEN});
    this.cancelPendingPositionTooltipIfNeeded();
  }
}

export const TooltipEl = defineAndMakeJsxFactory<TooltipJsxProps>(`mp-tooltip`, Tooltip);

const PortaledTooltip = portalify(TooltipEl);

/**
 * @todo pablo -- some test cases rely on the `mp-tooltip` element being present in a specific
 * DOM location, portalifying these to the document.body breaks these tests and there's no
 * clear way how to access or test the portalified element
 */
export const ExperimentalTooltipEl = isTestEnv()
  ? TooltipEl
  : function (...args: Parameters<typeof PortaledTooltip>) {
      const node = PortaledTooltip(...args);
      const defaultInsertHook = node.data.hook.insert;
      node.data.hook.insert = (vnode: VNode) => {
        defaultInsertHook?.(vnode);

        const tooltipEl = vnode.data.attachData.real as Tooltip;
        const placeholderEl = vnode.data.attachData.placeholder as HTMLElement;
        if (!Object.prototype.hasOwnProperty.call(vnode.data.props, `parentEl`) && !tooltipEl._parentEl) {
          // If the callsite hasn't specified parentEl prop, then set it to the parent element of the tooltip's original
          // location in the DOM.
          tooltipEl.parentEl = placeholderEl.parentElement;
        }
      };
      return node;
    };
/**
 * @deprecated
 * use common/components/tooltip/tooltip instead
 */
export const TooltipReact = panel2react<TooltipJsxProps>(`mp-tooltip`, Tooltip);
